import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { MoreHorizontal } from "lucide-react";

import {
  Badge, Button, ConfirmDialog, EmptyState, ErrorState, IconButton, Menu, MenuContent, MenuItem, MenuTrigger, Notice, PageHeader,
  Pagination, RouteTabs, SkeletonRows, Status, Surface, Textarea, cn, focusRing,
} from "../../../../components/ui";
import { businessApi } from "../../../../services/api";
import { useCanManagePayroll, useIsOwner } from "./HRPayrollLayout";
import ScheduleFormDialog from "./ScheduleFormDialog";
import ScheduleEmployees from "./ScheduleEmployees";
import { HR_PAYROLL_BASE, SCHEDULES_PATH, periodFacts, periodName, periodPath, schedulePath, stateOf } from "./hrPayroll";
import { CADENCE, LIFECYCLE_COPY, STATUS, dayText, ruleText } from "./payScheduleView";

const SECTIONS = ["employees", "periods", "history", "settings"];

function Facts({ items }) {
  return (
    <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
      {items.filter(Boolean).map(([label, value]) => (
        <div key={label}>
          <dt className="text-meta text-secondary">{label}</dt>
          <dd className="text-body-sm font-medium text-foreground">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Overview: the rule in words, what it pays next and how it stands today. */
function Overview({ schedule }) {
  const usage = schedule.usage || {};
  const windows = schedule.nextPayDates || [];
  return (
    <div className="space-y-6">
      <Surface padded>
        <h2 className="mb-3 text-panel-title text-foreground">How it pays</h2>
        <Facts items={[
          ["How often", CADENCE[schedule.frequency] || schedule.frequency],
          ["Periods", ruleText(schedule)],
          ["Pays from", schedule.firstWindowStart ? dayText(schedule.firstWindowStart) : schedule.status === "draft" ? "When the owner activates it" : "—"],
          ["Employees today", String(usage.currentAssignments ?? 0)],
          ["Payroll not finished", String(usage.unfinishedPeriods ?? 0)],
          ["Salaries approved, not yet paid", String(usage.unpaidSalaries ?? 0)],
        ]} />
        {schedule.notes ? <p className="mt-4 max-w-[75ch] text-body-sm text-secondary">{schedule.notes}</p> : null}
      </Surface>
      {schedule.status === "active" && windows.length ? (
        <section aria-labelledby="schedule-next" className="space-y-2">
          <h2 id="schedule-next" className="text-section-title text-foreground">Next periods</h2>
          <Surface>
            <ul className="divide-y divide-subtle">
              {windows.map((window, index) => (
                <li key={window.startDate} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5 px-4 py-2.5 text-body-sm">
                  <span className="text-foreground">{dayText(window.startDate)} – {dayText(window.endDate)}{index === 0 ? <span className="text-secondary"> · current</span> : null}</span>
                  <span className="text-secondary">Pay date {dayText(window.payDate)}</span>
                </li>
              ))}
            </ul>
          </Surface>
        </section>
      ) : null}
    </div>
  );
}

/**
 * Payroll periods of this schedule: the ones that exist (newest first, paged), the missed ones HR can add, and the
 * expected ones not created yet. Created periods open their own page; expected ones are only dates.
 */
function Periods({ schedule }) {
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ loading: true, error: false, rows: [], total: 0 });
  const [missing, setMissing] = useState([]);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let live = true;
    setData((current) => ({ ...current, loading: true, error: false }));
    businessApi.getPayrollPeriods({ scheduleId: schedule.id, page, limit: 25 })
      .then((res) => { if (live) setData({ loading: false, error: false, rows: res?.rows || [], total: Number(res?.total || 0) }); })
      .catch(() => { if (live) setData((current) => ({ ...current, loading: false, error: true })); });
    businessApi.getMissingPayrollPeriods()
      .then((res) => { if (live) setMissing((res?.data?.windows || []).filter((row) => Number(row.scheduleId) === Number(schedule.id))); })
      .catch(() => { if (live) setMissing([]); });
    return () => { live = false; };
  }, [schedule.id, page, attempt]);

  const created = new Set(data.rows.map((row) => String(row.start_date).slice(0, 10)));
  const projected = schedule.status === "active" && page === 1
    ? (schedule.nextPayDates || []).filter((row) => !created.has(row.startDate))
    : [];
  const pages = Math.max(Math.ceil(data.total / 25), 1);

  if (data.error) return <Surface><ErrorState title="Payroll periods couldn't be loaded" onRetry={() => setAttempt((n) => n + 1)} /></Surface>;
  return (
    <div className="space-y-6">
      {missing.length ? (
        <Notice tone="warning" title={`${missing.length} period${missing.length === 1 ? " has" : "s have"} no payroll`}>
          {missing.slice(0, 3).map((row) => `${dayText(row.startDate)} – ${dayText(row.endDate)}`).join(", ")}{missing.length > 3 ? ` and ${missing.length - 3} more` : ""}.
          {" "}Add them from <Link to={HR_PAYROLL_BASE} className={cn("rounded-control font-medium text-accent underline", focusRing)}>Payroll › Add missing period</Link>.
        </Notice>
      ) : null}
      <section aria-labelledby="schedule-periods" className="space-y-2">
        <div className="flex items-baseline justify-between gap-3">
          <h2 id="schedule-periods" className="text-section-title text-foreground">Payroll periods</h2>
          {!data.loading ? <p className="text-meta tabular-nums text-secondary">{data.total} created</p> : null}
        </div>
        <Surface>
          {data.loading && !data.rows.length ? <div className="p-4"><SkeletonRows rows={3} columns={3} label="Loading payroll periods" /></div>
            : !data.rows.length ? <EmptyState title="No payroll period yet" description="Periods are created when someone on this schedule has pay for those dates." />
              : (
                <>
                  {/* A management list: 13 px row identity, 12 px facts, the state as a plain word at the row's end.
                      The whole row opens the period, so it is one comfortable target on a phone. */}
                  <ul className="divide-y divide-subtle text-body-sm">
                    {data.rows.map((period) => (
                      <li key={period.id}>
                        <Link
                          to={periodPath(period.id)}
                          className={cn("flex min-h-touch flex-wrap items-center justify-between gap-x-4 gap-y-0.5 px-4 py-2.5 transition-colors hover:bg-hover", focusRing, "focus-visible:outline-offset-[-2px]")}
                        >
                          <span className="min-w-0">
                            <span className="block font-medium text-foreground">{periodName(period)}</span>
                            <span className="block text-meta text-secondary">{periodFacts(period)}</span>
                          </span>
                          <Status tone={stateOf(period).tone}>{stateOf(period).label}</Status>
                        </Link>
                      </li>
                    ))}
                  </ul>
                  <div className="border-t border-subtle px-4"><Pagination page={page} totalPages={pages} onPageChange={setPage} label="Payroll period pages" /></div>
                </>
              )}
        </Surface>
      </section>
      {projected.length ? (
        <section aria-labelledby="schedule-projected" className="space-y-2">
          <h2 id="schedule-projected" className="text-section-title text-foreground">Expected next</h2>
          <p className="text-body-sm text-secondary">Not created yet. Each period is created when it comes due, for whoever is on this schedule then.</p>
          <Surface>
            <ul className="divide-y divide-subtle">
              {projected.map((row) => (
                <li key={row.startDate} className="flex flex-wrap justify-between gap-x-4 gap-y-0.5 px-4 py-2.5 text-body-sm">
                  <span className="text-foreground">{dayText(row.startDate)} – {dayText(row.endDate)}</span>
                  <span className="text-secondary">Pay date {dayText(row.payDate)}</span>
                </li>
              ))}
            </ul>
          </Surface>
        </section>
      ) : null}
    </div>
  );
}

/** History: lifecycle changes and assignment events, newest first (a history collection, paged). */
function History({ schedule }) {
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ loading: true, error: false, events: [], total: 0 });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let live = true;
    setData((current) => ({ ...current, loading: true, error: false }));
    businessApi.getPayScheduleHistory(schedule.id, { page, limit: 25 })
      .then((res) => { if (live) setData({ loading: false, error: false, events: res?.data?.events || [], total: Number(res?.data?.total || 0) }); })
      .catch(() => { if (live) setData((current) => ({ ...current, loading: false, error: true })); });
    return () => { live = false; };
  }, [schedule.id, page, attempt]);
  const pages = Math.max(Math.ceil(data.total / 25), 1);
  if (data.error) return <Surface><ErrorState title="History couldn't be loaded" onRetry={() => setAttempt((n) => n + 1)} /></Surface>;
  return (
    <Surface>
      {data.loading && !data.events.length ? <div className="p-4"><SkeletonRows rows={4} columns={2} label="Loading history" /></div>
        : !data.events.length ? <EmptyState title="Nothing has happened yet" />
          : (
            <>
              <ol className="divide-y divide-subtle" aria-label="Pay schedule history">
                {data.events.map((event) => (
                  <li key={event.id} className="px-4 py-3">
                    <p className="text-body-sm text-foreground">
                      {event.text}
                      {event.memberName ? <> — <Link to={`/business/hr/employees/${event.memberId}/pay`} className={cn("rounded-control font-medium text-accent hover:underline", focusRing)}>{event.memberName}</Link></> : null}
                      {event.effectiveFrom ? <span className="text-secondary"> · {dayText(event.effectiveFrom)}{event.effectiveUntil ? ` – ${dayText(event.effectiveUntil)}` : " onwards"}</span> : null}
                    </p>
                    <p className="text-meta text-secondary">
                      {new Date(event.at).toLocaleString("en-PH", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}
                      {event.actorName ? ` · ${event.actorName}` : ""}
                      {event.reason ? ` · “${event.reason}”` : ""}
                    </p>
                  </li>
                ))}
              </ol>
              <div className="border-t border-subtle px-4"><Pagination page={page} totalPages={pages} onPageChange={setPage} label="History pages" /></div>
            </>
          )}
    </Surface>
  );
}

/** One lifecycle confirmation (C3, or C2 for the default): subject, current → target, what it affects, the verb. */
function LifecycleDialog({ action, schedule, onClose, onDone }) {
  const copy = LIFECYCLE_COPY[action];
  const [reason, setReason] = useState("");
  const needsReason = action === "archive" || action === "restore";
  const usage = schedule.usage || {};
  const target = { activate: "Active", archive: "Archived", restore: "Active", makeDefault: "Active, new employees start here" }[action];
  const first = (schedule.nextPayDates || [])[0];
  const facts = [
    { label: "Status", value: `${STATUS[schedule.status]?.label} → ${target}` },
    ...(action === "activate" || action === "restore") && first ? [{ label: "First period", value: `${dayText(first.startDate)} – ${dayText(first.endDate)} (the current period)` }] : [],
    ...(action !== "activate" ? [{ label: "Employees today", value: String(usage.currentAssignments ?? 0) }] : []),
    ...(action === "archive" ? [{ label: "Payroll not finished", value: String(usage.unfinishedPeriods ?? 0) }] : []),
  ];
  const run = async () => {
    if (action === "activate") await businessApi.activatePaySchedule(schedule.id, {});
    if (action === "archive") await businessApi.archivePaySchedule(schedule.id, { reason: reason.trim() });
    if (action === "restore") await businessApi.restorePaySchedule(schedule.id, { reason: reason.trim() });
    if (action === "makeDefault") await businessApi.makeDefaultPaySchedule(schedule.id);
    onDone(action);
  };
  return (
    <ConfirmDialog
      title={copy.title(schedule)}
      facts={facts}
      consequence={copy.consequence}
      confirmLabel={copy.confirm}
      cancelLabel="Go back"
      tone={action === "archive" ? "danger" : "default"}
      confirmDisabled={needsReason && reason.trim().length < 5}
      onConfirm={run}
      onClose={onClose}
    >
      {needsReason ? (
        <label className="block text-label text-secondary">
          Reason (at least 5 characters)
          <Textarea className="mt-1.5" rows={2} maxLength={500} value={reason} onChange={(event) => setReason(event.target.value)} />
        </label>
      ) : null}
    </ConfirmDialog>
  );
}

const DONE = {
  activate: (s) => `"${s.name}" is active. Put employees on it from Employees.`,
  archive: (s) => `"${s.name}" is archived. It pays nobody and creates no new periods.`,
  restore: (s) => `"${s.name}" is active again from its current period.`,
  makeDefault: (s) => `New employees now start on "${s.name}".`,
};

/**
 * HR › Payroll › Pay schedules › one schedule (Payroll Gate 3): route-backed sections Overview, Employees, Payroll
 * periods, History, Settings. Lifecycle actions are offered to the business owner only and each is confirmed; the
 * server refuses anything that would change payroll with Finance, leave employees unpaid or break history.
 */
export default function PayScheduleDetail() {
  const { scheduleId, section = "" } = useParams();
  const navigate = useNavigate();
  const canManage = useCanManagePayroll();
  const isOwner = useIsOwner();
  const [state, setState] = useState({ loading: true, error: "", schedule: null });
  const [dialog, setDialog] = useState(null); // lifecycle action | "edit" | "successor" | "delete"
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await businessApi.getPaySchedule(scheduleId);
      setState({ loading: false, error: "", schedule: res?.data || null });
    } catch (error) {
      setState({ loading: false, error: error?.response?.status === 404 ? "missing" : "failed", schedule: null });
    }
  }, [scheduleId]);
  // Another schedule (a replacement just created, a link): never show the previous one while the new one loads.
  useEffect(() => {
    setState({ loading: true, error: "", schedule: null });
    setNotice(null);
    load();
  }, [load]);

  if (state.loading) return <SkeletonRows rows={5} columns={3} label="Loading the pay schedule" />;
  if (state.error) {
    return (
      <Surface>
        {state.error === "missing"
          ? <EmptyState title="Pay schedule not found" description="It may belong to another business or have been deleted." action={<Button as={Link} to={SCHEDULES_PATH} size="sm">All pay schedules</Button>} />
          : <ErrorState title="The pay schedule couldn't be loaded" onRetry={load} />}
      </Surface>
    );
  }

  const schedule = state.schedule;
  const usage = schedule.usage || {};
  const used = schedule.status !== "draft" || usage.periods > 0 || usage.assignments > 0;
  const status = STATUS[schedule.status] || { label: schedule.status, tone: "neutral" };
  // The page shows the new state before it says what happened, so the two never disagree.
  const done = async (message) => {
    setDialog(null);
    await load();
    setNotice({ tone: "success", text: message });
  };

  const ownerActions = isOwner ? {
    draft: [{ key: "activate", label: "Activate", primary: true }],
    active: [
      ...(!schedule.isDefault ? [{ key: "makeDefault", label: "Make new employees start here" }] : []),
      { key: "successor", label: "Replace with a new schedule" },
      { key: "archive", label: "Archive", danger: true },
    ],
    archived: [{ key: "restore", label: "Restore", primary: true }],
  }[schedule.status] || [] : [];
  const primary = ownerActions.find((item) => item.primary);
  const secondary = ownerActions.filter((item) => !item.primary);

  const actions = (
    <>
      {primary ? <Button variant="primary" onClick={() => setDialog(primary.key)}>{primary.label}</Button> : null}
      {secondary.length ? (
        <Menu>
          <MenuTrigger asChild>
            <IconButton icon={MoreHorizontal} label={`More actions for ${schedule.name}`} />
          </MenuTrigger>
          <MenuContent>
            {secondary.map((item) => (
              <MenuItem key={item.key} tone={item.danger ? "danger" : undefined} onSelect={() => setDialog(item.key)}>{item.label}</MenuItem>
            ))}
          </MenuContent>
        </Menu>
      ) : null}
    </>
  );

  const base = schedulePath(schedule.id);
  return (
    <div className="space-y-6">
      <PageHeader
        title={schedule.name}
        description={`${CADENCE[schedule.frequency] || schedule.frequency} · ${ruleText(schedule)}`}
        meta={(
          <span className="flex flex-wrap items-center gap-2">
            <Status tone={status.tone}>{status.label}</Status>
            {schedule.isDefault ? <Badge>New employees start here</Badge> : null}
            {schedule.replacesScheduleId ? <Link to={schedulePath(schedule.replacesScheduleId)} className={cn("rounded-control text-body-sm text-accent hover:underline", focusRing)}>Replaces an earlier schedule</Link> : null}
          </span>
        )}
        actions={ownerActions.length ? actions : null}
        navigation={(
          <RouteTabs
            label="Pay schedule"
            items={[
              { to: base, label: "Overview", end: true },
              { to: `${base}/employees`, label: "Employees", count: usage.currentAssignments || 0 },
              { to: `${base}/periods`, label: "Payroll periods" },
              { to: `${base}/history`, label: "History" },
              { to: `${base}/settings`, label: "Settings" },
            ]}
          />
        )}
      />

      {schedule.status === "draft" ? (
        <Notice title="Draft — pays nobody yet">
          {isOwner ? "Check the rule in Settings, then activate it. After that, employees can be put on it." : "The business owner activates it. Until then nobody can be put on it and it creates no payroll period."}
        </Notice>
      ) : null}
      {schedule.status === "archived" ? (
        <Notice title="Archived">{schedule.archiveReason ? `“${schedule.archiveReason}”. ` : ""}It pays nobody and creates no new periods; its history stays here.</Notice>
      ) : null}
      {notice ? <Notice tone={notice.tone} role="status">{notice.text}</Notice> : null}

      {!section ? <Overview schedule={schedule} /> : null}
      {section === "employees" ? <ScheduleEmployees schedule={schedule} canManage={canManage} onChanged={load} onNotice={setNotice} /> : null}
      {section === "periods" ? <Periods schedule={schedule} /> : null}
      {section === "history" ? <History schedule={schedule} /> : null}
      {section === "settings" ? (
        <Surface padded className="space-y-4">
          <h2 className="text-panel-title text-foreground">Settings</h2>
          <Facts items={[
            ["Name", schedule.name],
            ["How often", CADENCE[schedule.frequency] || schedule.frequency],
            ["Periods", ruleText(schedule)],
            ["Code", schedule.code],
          ]} />
          <p className="max-w-[75ch] text-body-sm text-secondary">
            {used ? "This schedule has been used, so its cadence, cutoff and pay date stay as they are: its past periods depend on them. Rename it here; for a different rule, the owner replaces it with a new schedule." : "A draft that nothing has used can change in any way."}
          </p>
          <div className="flex flex-wrap gap-2">
            {canManage && schedule.status !== "archived" ? <Button onClick={() => setDialog("edit")}>{used ? "Rename or edit notes" : "Edit draft"}</Button> : null}
            {canManage && !used && !schedule.isDefault ? <Button variant="quiet" onClick={() => setDialog("delete")}>Delete draft</Button> : null}
          </div>
          {!isOwner ? (
            <p className="text-meta text-secondary">Only the business owner activates, replaces, archives or restores a pay schedule, and chooses where new employees start.</p>
          ) : null}
        </Surface>
      ) : null}
      {section && !SECTIONS.includes(section) ? <Surface><EmptyState title="This section doesn't exist" action={<Button as={Link} to={base} size="sm">Overview</Button>} /></Surface> : null}

      {LIFECYCLE_COPY[dialog] ? (
        <LifecycleDialog action={dialog} schedule={schedule} onClose={() => setDialog(null)} onDone={(action) => done(DONE[action](schedule))} />
      ) : null}
      {dialog === "edit" || dialog === "successor" ? (
        <ScheduleFormDialog
          mode={dialog}
          schedule={schedule}
          used={used}
          onClose={() => setDialog(null)}
          onSaved={async (saved) => {
            if (dialog === "successor") {
              setDialog(null);
              navigate(schedulePath(saved.id));
              return;
            }
            await done(`"${saved.name}" saved.`);
          }}
        />
      ) : null}
      {dialog === "delete" ? (
        <ConfirmDialog
          title={`Delete the draft "${schedule.name}"?`}
          consequence="It was never used, so nothing else changes. This cannot be undone."
          confirmLabel="Delete draft"
          cancelLabel="Keep it"
          tone="danger"
          onConfirm={async () => {
            await businessApi.deletePaySchedule(schedule.id);
            navigate(SCHEDULES_PATH);
          }}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </div>
  );
}
